"use client";

import { useState, useRef, useEffect } from "react";
import Link from "next/link";
import { User, LogOut } from "lucide-react";
import { useParams } from "next/navigation";
import { useCustomer } from "@/src/contexts/customer-context";
import { useLogOutModalStore } from "@/store/index";

export function UserMenu() {
  const { customer } = useCustomer();
  const { showModal } = useLogOutModalStore();
  const [isOpen, setIsOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);
  const params = useParams();
  const countryCode = (params?.countryCode as string) || "cu";

  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    }

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const handleLogout = () => {
    setIsOpen(false);
    showModal();
  };

  const isLoggedIn = !!customer;

  return (
    <div className="relative" ref={menuRef}>
      {/* User Icon Button */}
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="bg-green-main flex h-10 w-10 items-center justify-center rounded-lg text-white transition-colors hover:bg-[#047a2e]"
      >
        <User className="h-5 w-5" />
      </button>

      {/* Dropdown Menu */}
      {isOpen && (
        <div className="bg-cream absolute overflow-hidden top-12 right-0 z-50 w-64 rounded-2xl p-4 shadow-[0_10px_40px_rgba(0,0,0,0.2)]">
          {isLoggedIn ? (
            /* Logged In Menu */
            <div className="space-y-2">
              {/* User Info */}
              <div className="border-b border-gray-200 px-4 py-3">
                <p className="font-semibold text-gray-900">
                  {customer.first_name} {customer.last_name}
                </p>
                <p className="text-sm text-bre truncate">{customer.email}</p>
              </div>

              <Link
                href={`/${countryCode}/profile`}
                onClick={() => setIsOpen(false)}
                className="font-inter flex items-center gap-3 rounded-lg px-4 py-3 text-sm font-medium transition-colors hover:bg-white/50"
              >
                <User className="text-green-main h-5 w-5" />
                Mi Perfil
              </Link>
              <button
                onClick={handleLogout}
                className="font-inter flex w-full items-center gap-3 rounded-lg px-4 py-3 text-sm font-medium transition-colors hover:bg-white/50"
              >
                <LogOut className="text-green-main h-5 w-5" />
                Cerrar Sesión
              </button>
            </div>
          ) : (
            /* Not Logged In Menu */
            <div className="space-y-4">
              <Link
                href={`/${countryCode}/login`}
                onClick={() => setIsOpen(false)}
                className="bg-green-main font-inter block w-full rounded-lg px-6 py-3 text-center text-base font-semibold text-white transition-colors hover:bg-[#047a2e]"
              >
                Iniciar Sesión
              </Link>
              <div className="text-center">
                <span className="font-inter text-sm">¿No tienes cuenta?</span>
              </div>
              <Link
                href={`/${countryCode}/register`}
                onClick={() => setIsOpen(false)}
                className="border-green-main font-inter text-green-main hover:bg-green-main/10 block w-full rounded-lg border-2 bg-transparent px-6 py-3 text-center text-base font-semibold transition-colors"
              >
                Regístrate
              </Link>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
